/**
 * The Bookings tab's display half (rd-bookings; web-account-pages D8): the page
 * header with its Upcoming | Past control, and the active view's body. It takes
 * props and reads nothing: the Mini App's BookingsPage feeds it from the shell's
 * dashboard and its past-bookings read, the studio website's phone Bookings from
 * the website session's.
 *
 * Visual authority: docs/mockups/client-dashboard-redesign/bookings-tab.html.
 */
import { BookingCard } from '@/components/client/BookingCard/BookingCard';
import { EmptyBlock } from '@/components/client/EmptyBlock/EmptyBlock';
import { FlowBackButton } from '@/components/booking/FlowBackButton';
import { ChevronRightIcon, LocationIcon, PhoneIcon } from '@/pages/BookingHubPage/sections/icons';
import type { PastBookingListItem, UpcomingBooking } from '@/types/dashboard';
import styles from './BookingsPage.module.css';

export type BookingsTab = 'upcoming' | 'past';

const MONTH = new Intl.DateTimeFormat('en-US', { month: 'short' });

function VideoIcon({ className }: { className?: string } = {}) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="23 7 16 12 23 17 23 7" />
      <rect x="1" y="5" width="15" height="14" rx="2" />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string } = {}) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

/** The page's own header: back, the title and the Upcoming | Past control. */
export function BookingsHeader({
  activeTab,
  onTabChange,
  onBack,
}: {
  activeTab: BookingsTab;
  onTabChange: (tab: BookingsTab) => void;
  onBack: () => void;
}) {
  return (
    <>
      <div className={styles.pageHeaderSection}>
        <FlowBackButton className={styles.back} onClick={onBack} />
        <div className={styles.pageTitleBlock}>
          <h1 className={styles.pageTitle}>Bookings</h1>
          <p className={styles.pageSubtitle}>Your upcoming sessions &amp; history</p>
        </div>
      </div>
      <div className={styles.tabSegmented} role="tablist" aria-label="Bookings">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'upcoming'}
          className={activeTab === 'upcoming' ? `${styles.tabSegment} ${styles.active}` : styles.tabSegment}
          onClick={() => onTabChange('upcoming')}
        >
          Upcoming
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'past'}
          className={activeTab === 'past' ? `${styles.tabSegment} ${styles.active}` : styles.tabSegment}
          onClick={() => onTabChange('past')}
        >
          Past
        </button>
      </div>
    </>
  );
}

/** One thin, photo-less history row (mockup Frame 2). */
function PastRow({ item, onOpen }: { item: PastBookingListItem; onOpen: (id: string) => void }) {
  const cancelled = item.status === 'CANCELLED';
  const d = new Date(item.scheduledAt);
  const isVirtual = item.locationType === 'virtual';
  const byPhone = item.locationType === 'phone';
  return (
    <button
      type="button"
      className={cancelled ? `${styles.pastRow} ${styles.pastRowCancelled}` : styles.pastRow}
      onClick={() => onOpen(item.id)}
      aria-label={`View booking: ${item.serviceName}`}
    >
      <span className={styles.pastDate}>
        <span className={styles.pastMonth}>{MONTH.format(d)}</span>
        <span className={styles.pastDay}>{d.getDate()}</span>
      </span>
      <span className={styles.pastInfo}>
        <span className={styles.pastTitle}>{item.serviceName}</span>
        <span className={styles.pastMeta}>
          {item.providerName && (
            <>
              with {item.providerName}
              <span className={styles.pastDot}>·</span>
            </>
          )}
          {byPhone ? <PhoneIcon /> : isVirtual ? <VideoIcon /> : <LocationIcon />}
          {byPhone ? 'By phone' : isVirtual ? 'Virtual' : 'In person'}
        </span>
      </span>
      <span className={cancelled ? `${styles.statusPill} ${styles.statusPillCancelled}` : styles.statusPill}>
        {cancelled ? 'Cancelled' : 'Completed'}
      </span>
      <ChevronRightIcon className={styles.pastChev} />
    </button>
  );
}

export interface BookingsPast {
  status: 'loading' | 'error' | 'ready';
  items: PastBookingListItem[];
  hasMore: boolean;
  loadingMore: boolean;
  onMore: () => void;
}

export interface BookingsViewProps {
  tab: BookingsTab;
  upcoming: UpcomingBooking[];
  onOpen: (bookingId: string) => void;
  past: BookingsPast;
}

export function BookingsView({ tab, upcoming, onOpen, past }: BookingsViewProps) {
  return (
    <div data-testid="bookings-page">
      {tab === 'upcoming' ? (
        upcoming.length > 0 ? (
          <section className={styles.section}>
            {upcoming.map((booking) => (
              <BookingCard key={booking.id} booking={booking} onOpen={onOpen} variant="roomy" />
            ))}
          </section>
        ) : (
          // No Book banner under it (Gabriel 2026-11-03: "I don't think we even need it there"); Book lives on Home.
          <EmptyBlock
            testId="bookings-empty"
            title="No sessions yet"
            sub="When you book a session it will show up here, along with everything you've attended."
          />
        )
      ) : past.status === 'loading' ? (
        <div className={styles.stateBlock} data-testid="past-loading">
          Loading your history…
        </div>
      ) : past.status === 'error' ? (
        <div className={styles.stateBlock} data-testid="past-error">
          Couldn’t load your history.
        </div>
      ) : past.items.length > 0 ? (
        <section className={styles.section}>
          <div className={styles.pastList}>
            {past.items.map((item) => (
              <PastRow key={item.id} item={item} onOpen={onOpen} />
            ))}
          </div>
          {past.hasMore && (
            <button
              type="button"
              className={styles.showMore}
              onClick={past.onMore}
              disabled={past.loadingMore}
              data-testid="past-show-more"
            >
              {past.loadingMore ? 'Loading…' : 'Show earlier sessions'}
              <ChevronDownIcon />
            </button>
          )}
        </section>
      ) : (
        <EmptyBlock testId="bookings-empty" title="Nothing here yet" sub="Your attended and cancelled sessions will appear here." />
      )}
    </div>
  );
}
